import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import RefEntryModal from "./RefEntryModal";

/**
 * PaymentsList — CRUD page for the `payment` table.
 * Payment types are numeric (`pay_tp` SMALLINT) with a text label
 * (`pay_lit`). Follows the exact same layout as Codes/Payees.
 */
export default function PaymentsList({ profile, canWrite }) {
  const isAdmin = canWrite;
  const [rowsAll, setRowsAll] = useState(null);
  const [err, setErr] = useState("");
  const [tpFilter, setTpFilter] = useState("");
  const [litFilter, setLitFilter] = useState("");
  const [modal, setModal] = useState(null);

  const load = () => {
    setRowsAll(null);
    api.listPayments()
      .then((r) => setRowsAll(r || []))
      .catch((e) => setErr(e?.response?.data?.detail || e.message || "Failed to load payments"));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!rowsAll) return null;
    const t = tpFilter.trim().toLowerCase();
    const l = litFilter.trim().toLowerCase();
    if (!t && !l) return rowsAll;
    return rowsAll.filter((r) => {
      const tp = String(r.pay_tp ?? "").toLowerCase();
      const lit = String(r.pay_lit ?? "").toLowerCase();
      return (!t || tp.includes(t)) && (!l || lit.includes(l));
    });
  }, [rowsAll, tpFilter, litFilter]);

  const anyFilter = tpFilter || litFilter;
  const clearFilters = () => { setTpFilter(""); setLitFilter(""); };
  const filterInputStyle = {
    width: "100%", padding: "4px 8px", fontSize: 13,
    border: "1px solid #555", borderRadius: 4, fontWeight: "normal",
  };

  const fields = [
    { name: "pay_tp", label: "Pay type #", type: "number", required: true,
      pk: true, hint: "0-32767 (smallint)" },
    { name: "pay_lit", label: "Label", maxLength: 50, required: true,
      hint: "max 50 chars", gridSpan: 2 },
  ];

  const handleSave = async (payload) => {
    if (modal.mode === "create") {
      await api.createPayment({
        pay_tp: Number(payload.pay_tp),
        pay_lit: payload.pay_lit,
      });
    } else {
      await api.updatePayment(modal.row.pay_tp, { pay_lit: payload.pay_lit });
    }
    setModal(null); load();
  };
  const handleDelete = async () => {
    await api.deletePayment(modal.row.pay_tp);
    setModal(null); load();
  };

  return (
    <section data-testid="payments-section">
      <p className="section-title">Payments</p>
      <div className="card list-card txn-card" data-testid="payments-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            <span className="list-count count" data-testid="payments-count">
              {rowsAll
                ? `${(rows || []).length} of ${rowsAll.length} payment${rowsAll.length === 1 ? "" : "s"}`
                : "Loading…"}
            </span>
            {anyFilter && (
              <button className="btn-ghost btn-sm" onClick={clearFilters}
                      data-testid="payments-flt-clear">Clear filters</button>
            )}
          </div>
          {isAdmin && (
            <div className="list-header-right txn-header-right">
              <button className="btn-primary"
                      data-testid="payments-new-btn"
                      onClick={() => setModal({ mode: "create", row: {} })}
              >+ New payment</button>
            </div>
          )}
        </div>

        <div className="list-scroll txn-scroll" data-testid="payments-scroll">
          {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
          {!rows ? (
            <div className="loading-row"><span className="spinner" /> Loading payments…</div>
          ) : (
            <table className="list-table list-table-compact txn-table txn-table-compact" data-testid="payments-table">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th style={{ width: "22%" }}>Pay type #</th>
                  <th>Label</th>
                </tr>
                <tr className="list-head-filters txn-head-filters">
                  <th>
                    <input data-testid="payments-filter-tp" type="text"
                      placeholder="Filter #" value={tpFilter}
                      onChange={(e) => setTpFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                  <th>
                    <input data-testid="payments-filter-lit" type="text"
                      placeholder="Filter label" value={litFilter}
                      onChange={(e) => setLitFilter(e.target.value)}
                      style={filterInputStyle} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "18px",
                                             color: "var(--muted, #666)" }}
                        data-testid="payments-empty">
                      No payments match the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.pay_tp}
                        data-testid={`payments-row-${r.pay_tp}`}
                        onClick={() => setModal({ mode: "edit", row: r })}
                        style={{ cursor: "pointer" }}>
                      <td style={{ fontFamily: "var(--font-mono, monospace)" }}>{r.pay_tp}</td>
                      <td>{r.pay_lit}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <RefEntryModal
          testIdPrefix="payments-modal"
          mode={modal.mode}
          title={modal.mode === "create" ? "New payment" : `Edit payment ${modal.row.pay_tp}`}
          fields={fields}
          initial={modal.row}
          canWrite={isAdmin}
          onSave={handleSave}
          onDelete={modal.mode === "edit" ? handleDelete : undefined}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}
